import { useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { CHAT_BLK, URIS } from '../constants';
import { ChatBlkQueryService, ChatBlkTelemetryService } from '../services';

import { ChatBlkThunkDispatch } from '../store/store';
import { sendQuery } from '../store/thunk';
import {
    ChatblkMfeFunctionMap,
    ChatBlkQuery,
    ChatBlkQueryScopeEnum,
    QueryState,
    QueryTypeEnum,
    telemetryFunctionMap,
} from '../types';
import { uuidv4 } from '../utils';
import useChatBlkStore from './useChatBlkStore';
// import useQueryDispatch from './useQueryDispatch';

const { INVESTMENT_ADVICE_SCOPE, OUT_OF_SCOPE } = ChatBlkQueryScopeEnum;

export const useServerMessageEvents = (query: ChatBlkQuery) => {
    const { followUpPrompts = [], scope, sources, response, id } = query || {};

    const chatService = ChatBlkQueryService.getInstance();

    const chatBubbleResponseRef = useRef<HTMLBxfChatBubbleElement>(null);
    const dispatch = useDispatch<ChatBlkThunkDispatch>();
    const {
        updateQuery,
        isRequiredFeedbackSubmitted,
        setIsRequiredFeedbackSubmitted,
        mostRecentQueryId,
    } = useChatBlkStore();

    const isOutOfScope = [INVESTMENT_ADVICE_SCOPE, OUT_OF_SCOPE].includes(
        scope
    );
    const followUpQuestions = useMemo(
        () =>
            !isOutOfScope &&
            followUpPrompts.map((f) => ({ question: f.prompt, id: f.id })),
        [isOutOfScope, followUpPrompts]
    );
    const similarQuestions = useMemo(
        () => isOutOfScope && followUpPrompts.map((f) => f.prompt),
        [isOutOfScope, followUpPrompts]
    );

    const hasFooter =
        mostRecentQueryId === query.hashId && query.state === QueryState.LOADED;

    const hasSources = !!sources?.length;
    //&& !isProgressiveLoading && !isQueryCanceled;

    // Enable custom feedback for internal users only
    const email = chatService.email ?? '';
    const hasCustomFeedback =
        email.includes('blackrock.com') ||
        URIS.PRE_PROD_HOSTS.includes(window.location.hostname);

    const [isSourcesExpanded, setIsSourcesExpanded] = useState(true);

    const hideCopyButton = !window['__CYPRESS_SHOW_COPY_BUTTON__'];

    const handleProdFeedbackSubmitted = ({
        detail: { additionalFeedback: feedbackComment },
    }: CustomEvent) => {
        setIsRequiredFeedbackSubmitted(true);

        const updatedProps = { ...query, feedbackComment } as ChatBlkQuery;
        ChatBlkTelemetryService.trackFeedback(updatedProps);
        updateQuery({
            hashId: query.hashId,
            updatedProps,
        });
        chatBubbleResponseRef.current?.closeFooter();
    };

    /**
     * Extra feedback not required in prod for thumbs up -> Submit feedback and call the callback
     * Otherwise, extra feedback is required. Feedback will be submitted and callback will be
     * called upon submission of custom feedback
     *
     * @param {CustomEvent} event - event details that indicate thumbs up or down
     */
    const handleFeedbackIconToggle = ({
        detail: { isThumbsUp },
    }: CustomEvent): void =>
        ChatBlkTelemetryService.track(
            telemetryFunctionMap.CHATBLK_MFE_FUNCTION_CHAT_BUBBLE_WAS_THIS_HELPFUL_CLICKED,
            { isThumbsUp },
            id
        );

    /**
     * Handles when custom/additional feedback is visible to the user at the footer of its respective bubble.
     *
     * @param {CustomEvent} event - The custom event containing the detail with the isExpanded property.
     *                              Specifically, event.detail.isExpanded indicates whether the feedback section
     *                              is expanded or collapsed.
     *
     */
    const handleFeedbackToggle = ({
        detail: { isExpanded },
    }: CustomEvent): void =>
        ChatBlkTelemetryService.track(
            telemetryFunctionMap.CHATBLK_MFE_FUNCTION_CHAT_BUBBLE_FEEDBACK_EXPANDED,
            { isExpanded },
            id
        );

    const handleCopyButtonClicked = () => {
        ChatBlkTelemetryService.track(
            telemetryFunctionMap.CHATBLK_MFE_FUNCTION_COPY_CLICKED,
            null,
            id
        );
        navigator.clipboard.writeText(response).catch((err) => {
            console.error(`[${CHAT_BLK}] Could not copy text:`, err);
        });
    };

    const handleFollowUpQuestionClicked = async (event: CustomEvent) => {
        const { question, id } = event.detail;

        dispatch(
            sendQuery({
                prompt: question,
                chatExchangeId: id,
                hashId: uuidv4(),
                queryType: QueryTypeEnum.QUERY,
                telemetryFunction:
                    telemetryFunctionMap.CHATBLK_MFE_FUNCTION_CHAT_BUBBLE_FOLLOW_UP_SUGGESTION_CLICKED,
            })
        );
    };

    const handleSimilarQuestionClicked = async (event: CustomEvent) => {
        const { question } = event.detail;

        dispatch(
            sendQuery({
                prompt: question,
                chatExchangeId: uuidv4(),
                hashId: uuidv4(),
                queryType: QueryTypeEnum.QUERY,
                telemetryFunction:
                    telemetryFunctionMap.CHATBLK_MFE_FUNCTION_CHAT_BUBBLE_FOLLOW_UP_SUGGESTION_CLICKED,
            })
        );
    };

    const handleSourcesAccordionToggled = (event: CustomEvent) => {
        const { wasExpanded } = event.detail;
        let telemetryFunction: ChatblkMfeFunctionMap[keyof ChatblkMfeFunctionMap] =
            telemetryFunctionMap.CHATBLK_MFE_FUNCTION_SOURCES_ACCORDION_EXPANDED;
        if (wasExpanded) {
            setIsSourcesExpanded(true);
        } else {
            telemetryFunction =
                telemetryFunctionMap.CHATBLK_MFE_FUNCTION_SOURCES_ACCORDION_COLLAPSED;
            setTimeout(() => {
                setIsSourcesExpanded(false);
            }, 200); // Delay to avoid child elements being tabbable when the accordion is collapsed
        }
        ChatBlkTelemetryService.track(telemetryFunction, null, id);
    };

    return {
        chatBubbleResponseRef,
        hideCopyButton,
        isOutOfScope,
        hasFooter,
        hasSources,
        similarQuestions,
        isSourcesExpanded,
        followUpQuestions,
        isRequiredFeedbackSubmitted,
        hasCustomFeedback,
        handleProdFeedbackSubmitted,
        handleFeedbackIconToggle,
        handleFeedbackToggle,
        handleCopyButtonClicked,
        handleFollowUpQuestionClicked,
        handleSimilarQuestionClicked,
        handleSourcesAccordionToggled,
    };
};

export default useServerMessageEvents;
